import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getJobs, Job, retryJob } from '../lib/api';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_ICONS: Record<string, React.ReactNode> = {
  queued: <Clock className="h-4 w-4 text-slate-400" />,
  fetching: <Loader2 className="h-4 w-4 text-blue-400 animate-spin" />,
  analyzing: <Loader2 className="h-4 w-4 text-amber-400 animate-spin" />,
  done: <CheckCircle className="h-4 w-4 text-emerald-400" />,
  failed: <XCircle className="h-4 w-4 text-red-400" />,
  deferred: <AlertTriangle className="h-4 w-4 text-orange-400" />,
};

export default function Jobs() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [blockFilter, setBlockFilter] = useState<string>('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', statusFilter, blockFilter],
    queryFn: () => getJobs(statusFilter || undefined, blockFilter || undefined),
  });

  const retryMutation = useMutation({
    mutationFn: retryJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const blocks = [
    'keyword_search', 'keyword_generation', 'grid_search', 'perspective_search',
    'seo_audit', 'technical_health', 'content_quality', 'advanced_aeo_signals'
  ];

  const statuses = ['queued', 'fetching', 'analyzing', 'done', 'failed', 'deferred'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-100">Job Management</h1>
        <div className="flex gap-2">
          <select
            className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {statuses.map(s => (
              <option key={s} value={s} className="capitalize">{s.replace('_', ' ')}</option>
            ))}
          </select>
          <select
            className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            value={blockFilter}
            onChange={(e) => setBlockFilter(e.target.value)}
          >
            <option value="">All Blocks</option>
            {blocks.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-400 uppercase bg-slate-800/50">
              <tr>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Block</th>
                <th className="px-4 py-3">Business ID</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    Loading jobs...
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                    No jobs found matching the filters.
                  </td>
                </tr>
              ) : (
                data?.items.map((job: Job) => (
                  <tr key={job.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {STATUS_ICONS[job.status] || <Clock className="h-4 w-4" />}
                        <span className="capitalize text-slate-200">{job.status.replace('_', ' ')}</span>
                      </div>
                      {job.error && (
                        <p className="text-xs text-red-400 mt-1 truncate max-w-[200px]" title={job.error}>
                          {job.error}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-300">{job.block}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                      {job.business_id.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {job.created_at ? format(new Date(job.created_at), 'MMM d, HH:mm') : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {job.status === 'failed' && (
                        <button
                          onClick={() => retryMutation.mutate(job.id)}
                          disabled={retryMutation.isPending}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-md transition-colors disabled:opacity-50"
                        >
                          {retryMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && data.total > data.page_size && (
          <div className="px-4 py-3 border-t border-slate-800 text-xs text-slate-500 flex justify-between items-center">
            <span>Page {data.page}</span>
            <span>{data.total} total jobs</span>
          </div>
        )}
      </div>
    </div>
  );
}
